import React from "react";
import { AlertTriangle, Info } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Tabs, TabsList, TabsTrigger } from "../../../ui/tabs";
import { cn } from "../../../lib/utils";

/**
 * The small, repeated pieces of the Player Insights tab: card shell, section
 * heading, segmented toggle, insight pill, empty state, metric block.
 *
 * Every card in the tab shares one header pattern — title left, contextual
 * control right (§3) — so it lives here rather than being re-typed per card.
 *
 * Colour comes from the ASTRA theme tokens only: primary for the casino series,
 * chart-sports for the sports series, chart-positive reserved for GGR,
 * chart-warning for staleness, destructive for risk.
 */

/**
 * Built on the shared Card/CardHeader/CardTitle/CardDescription/CardContent,
 * not a reinvented shell. Their defaults (24px padding, 24px title) are sized
 * for a single hero card or dialog; this page packs 3-4 cards per row, so
 * every slot's padding and type size is overridden to the brief's 16px/12px
 * density (§5.5) while keeping the same structural pieces (and CardDescription's
 * own text-sm text-muted-foreground already matches the subtitle role as-is).
 */
export const InsightCard = React.forwardRef(
  ({ title, subtitle, control, children, className, bodyClassName, ...props }, ref) => (
    <Card ref={ref} className={cn("flex flex-col", className)} {...props}>
      {(title || control) && (
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0 px-4 pb-3 pt-4">
          <div className="min-w-0">
            <CardTitle className="text-sm font-semibold leading-tight tracking-normal">{title}</CardTitle>
            {subtitle && <CardDescription className="mt-0.5 leading-tight">{subtitle}</CardDescription>}
          </div>
          {control && <div className="shrink-0">{control}</div>}
        </CardHeader>
      )}
      <CardContent className={cn("flex-1 px-4 pb-4 pt-0", bodyClassName)}>{children}</CardContent>
    </Card>
  )
);
InsightCard.displayName = "InsightCard";

/**
 * Anchor-target section heading. Quiet by design — the cards carry the page.
 *
 * The scroll margin is composed from the same offsets the sticky chrome uses,
 * so an anchor jump lands the heading just below the nav however tall the app
 * tab bar and identity header happen to render.
 */
const ANCHOR_SCROLL_MARGIN =
  "calc(var(--sticky-tabs-offset, 99px) + var(--pi-header-height, 66px) + var(--pi-nav-height, 37px) + 16px)";

export function SectionHeading({ id, eyebrow, title, description, actions }) {
  return (
    <div id={id} className="mb-3 flex items-end justify-between gap-4" style={{ scrollMarginTop: ANCHOR_SCROLL_MARGIN }}>
      <div>
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{eyebrow}</div>
        <h2 className="mt-1 text-lg font-semibold leading-tight text-foreground">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions}
    </div>
  );
}

/**
 * Segmented control used for Daily/Weekly/Monthly and Casino/Sports/Both.
 *
 * This is the shared Tabs primitive used as a controlled picker — Root + List
 * + Trigger only, no TabsContent, since the parent section swaps its own
 * content off the same `value` rather than letting Tabs render panels. Its
 * active-state treatment (data-[state=active]:bg-*, shadow-sm) is already
 * exactly the toggle look this page wants; only sizing is overridden for the
 * "sm" density these cards need, and the active fill is repointed from
 * TabsTrigger's default bg-background to bg-card, since this toggle always
 * sits inside a card surface, not directly on the page background.
 */
export function SegmentedToggle({ options, value, onChange, size = "sm", ariaLabel }) {
  return (
    <Tabs value={value} onValueChange={onChange}>
      <TabsList aria-label={ariaLabel} className="h-auto gap-0.5 border bg-muted p-0.5">
        {options.map((option) => (
          <TabsTrigger
            key={option.value}
            value={option.value}
            className={cn(
              "font-medium data-[state=active]:bg-card data-[state=active]:shadow-sm",
              size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"
            )}
          >
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

const PILL_TONES = {
  neutral: "border-border bg-muted",
  accent: "border-primary/25 bg-primary/5",
  positive: "border-chart-positive/30 bg-chart-positive/5",
  warning: "border-chart-warning/35 bg-chart-warning/10",
};

const PILL_ICON_TONES = {
  neutral: "text-muted-foreground",
  accent: "text-primary",
  positive: "text-chart-positive",
  warning: "text-chart-warning",
};

/**
 * Insight pill — icon plus one plain sentence. The page is meant to tell the
 * operator things rather than just chart them, so this is used in every section.
 */
export function InsightPill({ icon: Icon = Info, children, tone = "neutral", className }) {
  return (
    <div className={cn("flex items-start gap-2 rounded-md border px-3 py-2 text-foreground", PILL_TONES[tone], className)}>
      <Icon className={cn("mt-px h-3.5 w-3.5 shrink-0", PILL_ICON_TONES[tone])} aria-hidden="true" />
      <p className="text-sm leading-snug">{children}</p>
    </div>
  );
}

/**
 * §7.3 — a card with nothing to show keeps its position and its height, and says
 * why in one muted sentence. The layout never collapses.
 */
export function EmptyState({
  message = "Not enough history yet — this profile builds after a few sessions.",
  minHeight = 120,
}) {
  return (
    <div
      className="flex items-center justify-center rounded-md border border-dashed px-4 text-center"
      style={{ minHeight }}
    >
      <p className="max-w-[28ch] text-sm leading-snug text-muted-foreground">{message}</p>
    </div>
  );
}

/** Amber inline note used for staleness and support caveats. */
export function WarningNote({ children }) {
  return (
    <div className="flex items-start gap-2 text-sm text-chart-warning">
      <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}
